<script setup>
// NOTICE: KAPTION-INTEGRATION
// two-step-verification.RECOVERY.2 — an administrator resets another member's
// second-step methods (for someone who lost every one). Shown only with the
// feature on and never for yourself; the server re-checks all of it.
import { computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';
import Button from 'dashboard/components-next/button/Button.vue';
import Dialog from 'dashboard/components-next/dialog/Dialog.vue';
import KaptionTwoStepAPI from 'dashboard/api/kaptionTwoStep';
import { useKaptionTwoStep } from 'dashboard/composables/useKaptionTwoStep';

const props = defineProps({
  userId: { type: Number, required: true },
  name: { type: String, default: '' },
});

const { t } = useI18n();
const { isEnabled } = useKaptionTwoStep();
const currentUserId = useMapGetter('getCurrentUserID');
const accountId = useMapGetter('getCurrentAccountId');
const currentRole = useMapGetter('getCurrentRole');

const dialogRef = ref(null);
const busy = ref(false);

const visible = computed(
  () =>
    isEnabled.value &&
    currentRole.value === 'administrator' &&
    props.userId !== currentUserId.value
);

const reset = async () => {
  busy.value = true;
  try {
    await KaptionTwoStepAPI.resetForUser(accountId.value, props.userId);
    useAlert(t('KAPTION_TWO_STEP.RESET.SUCCESS'));
    dialogRef.value?.close();
  } catch (e) {
    const code = e?.response?.data?.error_code;
    useAlert(
      code === 'support_required'
        ? t('KAPTION_TWO_STEP.RESET.SUPPORT_REQUIRED')
        : t('KAPTION_TWO_STEP.RESET.ERROR')
    );
  } finally {
    busy.value = false;
  }
};
</script>

<!-- eslint-disable-next-line vue/no-root-v-if -->
<template>
  <template v-if="visible">
    <Button
      ghost
      type="button"
      icon="i-lucide-shield-off"
      class="!px-2"
      :label="$t('KAPTION_TWO_STEP.RESET.ACTION')"
      data-testid="kpt-two-step-reset"
      @click.prevent="dialogRef?.open()"
    />
    <Dialog
      ref="dialogRef"
      type="alert"
      :title="$t('KAPTION_TWO_STEP.RESET.CONFIRM_TITLE')"
      :description="$t('KAPTION_TWO_STEP.RESET.CONFIRM_DESC', { name })"
      :confirm-button-label="$t('KAPTION_TWO_STEP.RESET.CONFIRM')"
      :is-loading="busy"
      @confirm="reset"
    />
  </template>
</template>
